import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  CreateUserPayload,
  ExportUserRole,
  UpdateUserPayload,
  User,
} from "@web-app/types";
import { handleMutationError } from "@web-app/utils/errorHandler";

import {
  create,
  deactivate,
  getExcel,
  getOne,
  list,
  remove,
  update,
  updateCurrentWorkspace,
} from "../services/users";

const queryKey = "users";

export const useUsers = (workspaceId: string) => {
  return useQuery({
    queryFn: async () => {
      const response = await list(workspaceId);
      return response;
    },
    queryKey: [queryKey],
    refetchOnWindowFocus: false,
    select: (users) => {
      return new Map(users.map((user) => [user.id, user]));
    },
  });
};

export const useGetUser = (userId: string) => {
  return useQuery({
    enabled: !!userId,
    queryFn: getOne,
    queryKey: ["single-user", userId],
    refetchOnWindowFocus: false,
    staleTime: 0, // 0 means data is considered stale immediately
  });
};
export const useExportUsers = () => {
  return useMutation({
    mutationFn: async (roles: ExportUserRole[]) => {
      const blob = await getExcel(roles);
      return blob;
    },
    onError: (err: unknown) => {
      handleMutationError(err, "export users");
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `users_${new Date().toISOString()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useUserMutation = () => {
  const queryClient = useQueryClient();

  const createUser = useMutation({
    mutationFn: async (payload: CreateUserPayload) => {
      const response = await create(payload);
      return response;
    },
    onError: (err: unknown) => {
      handleMutationError(err, "create user");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const updateUser = useMutation({
    mutationFn: async (params: {
      id: string;
      payload: Partial<UpdateUserPayload>;
    }) => {
      const response = await update(params.id, params.payload);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      const sessionUserData = queryClient.getQueryData<User>(["sessionUser"]);
      if (sessionUserData && sessionUserData.id === variables.id) {
        queryClient.setQueryData(["sessionUser"], {
          ...sessionUserData,
          ...variables.payload,
        });
      }
    },
  });

  const switchWorkspace = useMutation({
    mutationFn: async (workspaceId: string) => {
      const response = await updateCurrentWorkspace(workspaceId);
      return response;
    },
    onError: (err: unknown) => {
      // 401 errors are handled by the HTTP interceptor (redirect to signin)
      // Log other errors for debugging
      handleMutationError(err, "switch workspace");
    },
  });

  const deactivateUser = useMutation({
    mutationFn: async (userId: string) => {
      const response = await deactivate({ userId });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const removeUser = useMutation({
    mutationFn: async ({
      userId,
      workspaceId,
    }: {
      userId: string;
      workspaceId: string;
    }) => {
      const response = await remove({
        userId,
        workspaceId,
      });
      return response;
    },
    onSuccess: (_, { userId }) => {
      queryClient.setQueryData(["users"], (existingUsers: User[]) => {
        return existingUsers.filter((user) => user.id !== userId);
      });
    },
  });

  return {
    createUser,
    deactivateUser,
    removeUser,
    switchWorkspace,
    updateUser,
  };
};
